import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, Inject } from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { MESSAGES } from "src/app/shared/constants/messages.constant";

export interface AddCourseDialogData {
  title: string;
  courseCodeLabel: string;
  courseNameLabel: string;
  cancelLabel: string;
  confirmLabel: string;
}

export interface AddCourseDialogResult {
  code: string;
  name: string;
  displayValue: string;
}

type AddCourseFormGroup = FormGroup<{
  courseCode: FormControl<string>;
  courseName: FormControl<string>;
}>;

@Component({
  selector: "app-add-course-dialog",
  templateUrl: "./add-course-dialog.component.html",
  styleUrls: ["./add-course-dialog.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddCourseDialogComponent {
  readonly messages = MESSAGES;
  readonly addCourseForm: AddCourseFormGroup = new FormGroup({
    courseCode: new FormControl("", {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.maxLength(50),
        Validators.pattern(/\S/),
      ],
    }),
    courseName: new FormControl("", {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.maxLength(150),
        Validators.pattern(/\S/),
      ],
    }),
  });

  constructor(
    private readonly dialogRef: MatDialogRef<
      AddCourseDialogComponent,
      AddCourseDialogResult | undefined
    >,
    @Inject(MAT_DIALOG_DATA) public readonly data: AddCourseDialogData,
  ) {}

  close(): void {
    this.dialogRef.close();
  }

  submit(): void {
    this.addCourseForm.markAllAsTouched();
    if (this.addCourseForm.invalid) {
      return;
    }

    const code = this.addCourseForm.controls.courseCode.value.trim();
    const name = this.addCourseForm.controls.courseName.value.trim();

    this.dialogRef.close({
      code,
      name,
      displayValue: `${code} - ${name}`,
    });
  }

  getControlError(control: AbstractControl<string, string>): string {
    if (control.hasError("required") || control.hasError("pattern")) {
      return control === this.addCourseForm.controls.courseCode
        ? MESSAGES.COURSE_PICKER.COURSE_CODE_REQUIRED
        : MESSAGES.COURSE_PICKER.COURSE_NAME_REQUIRED;
    }

    return "";
  }
}